import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { ModelCodeService } from 'src/app/services/model-code/model-code.service';
import { ModelCodeModalService } from 'src/app/services/model-code-modal/model-code-modal.service';
import { CarModelService } from 'src/app/services/car-model/car-model.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-add-edit-model-codes',
  templateUrl: './add-edit-model-codes.component.html',
  styleUrls: ['./add-edit-model-codes.component.scss']
})
export class AddEditModelCodesComponent extends DestructorComponent implements OnInit {

  @Output() modelCodeAdded = new EventEmitter<void>();
  @Output() modelCodeUpdated = new EventEmitter<void>();

  modelCodeForm!: FormGroup;
  isLoading = false;
  isEditMode = false;
  selectedModelCode: any = null;

  // Dropdown data
  carModels: any[] = [];
  isLoadingCarModels = false;

  constructor(
    private fb: FormBuilder,
    private _modelCodeService: ModelCodeService,
    private _modelCodeModalService: ModelCodeModalService,
    private _carModelService: CarModelService
  ) {
    super();
    this.initializeForm();
  }

  ngOnInit(): void {
    this.setupModalListener();
    this.loadCarModels();
  }

  private initializeForm(): void {
    this.modelCodeForm = this.fb.group({
      model: ['', [Validators.required]],
      code: ['', [Validators.required, Validators.minLength(1)]],
      length: ['', [Validators.required, Validators.min(0)]],
      width: ['', [Validators.required, Validators.min(0)]],
      height: ['', [Validators.required, Validators.min(0)]]
    });
  }

  // Getter methods for form controls
  get model() { return this.modelCodeForm.get('model'); }
  get code() { return this.modelCodeForm.get('code'); }
  get length() { return this.modelCodeForm.get('length'); }
  get width() { return this.modelCodeForm.get('width'); }
  get height() { return this.modelCodeForm.get('height'); }

  private loadCarModels(): void {
    this.isLoadingCarModels = true;
    this.subscriptions.push(
      this._carModelService.GetData('1', '', { paginate: false }).subscribe({
        next: (response: any) => {
          if (response.status && response.data) {
            this.carModels = response.data.data || response.data || [];
          } else {
            this.carModels = [];
          }
          this.isLoadingCarModels = false;
        },
        error: (error: any) => {
          console.error('Error loading car models:', error);
          this.carModels = [];
          this.isLoadingCarModels = false;
        }
      })
    );
  }

  private setupModalListener(): void {
    const modalElement = document.getElementById('kt_modal_model_code_add_edit');
    if (modalElement) {
      modalElement.addEventListener('shown.bs.modal', () => {
        const serviceModelCode = this._modelCodeModalService.getSelectedModelCode();
        if (serviceModelCode) {
          this.isEditMode = true;
          this.selectedModelCode = serviceModelCode;
          this.populateForm(this.selectedModelCode);
        } else {
          this.isEditMode = false;
          this.selectedModelCode = null;
          this.resetForm();
        }
      });

      modalElement.addEventListener('hidden.bs.modal', () => {
        this._modelCodeModalService.clearSelectedModelCode();
        this.resetForm();
      });
    }
  }

  private populateForm(modelCode: any): void {
    this.modelCodeForm.patchValue({
      model: modelCode.model?._id || modelCode.model || '',
      code: modelCode.code || '',
      length: modelCode.length || '',
      width: modelCode.width || '',
      height: modelCode.height || ''
    });
  }

  private resetForm(): void {
    this.modelCodeForm.reset();
    this.isLoading = false;
    this.markFormGroupUntouched(this.modelCodeForm);
  }

  private markFormGroupUntouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsUntouched();
      control?.markAsPristine();
    });
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }

  private closeModal(): void {
    const modalElement = document.getElementById('kt_modal_model_code_add_edit');
    if (modalElement) {
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) {
        modal.hide();
      }
    }
  }

  onSubmit(): void {
    if (this.modelCodeForm.valid && !this.isLoading) {
      this.isLoading = true;

      const formData = this.modelCodeForm.value;

      // Remove empty fields
      const submitData: any = {};
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== '') {
          submitData[key] = formData[key];
        }
      });

      const apiCall = this.isEditMode
        ? this._modelCodeService.UpdateData(this.selectedModelCode._id, submitData)
        : this._modelCodeService.AddData(submitData);

      this.subscriptions.push(
        apiCall.subscribe({
          next: (response: any) => {
            this.isLoading = false;
            console.log('Model code operation successful:', response);

            // Show success message
            Swal.fire({
              text: this.isEditMode
                ? "Model code has been successfully updated!"
                : "Model code has been successfully created!",
              icon: "success",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn btn-primary"
              }
            }).then((result: any) => {
              if (result.isConfirmed) {
                // Close the modal
                this.closeModal();

                // Emit appropriate event
                if (this.isEditMode) {
                  this.modelCodeUpdated.emit();
                } else {
                  this.modelCodeAdded.emit();
                }

                // Reset the form
                this.resetForm();
              }
            });
          },
          error: (error: any) => {
            this.isLoading = false;
            console.error('Model code operation failed:', error);

            // Show error message
            Swal.fire({
              text: error.error?.message || "An error occurred. Please try again.",
              icon: "error",
              buttonsStyling: false,
              confirmButtonText: "Ok, got it!",
              customClass: {
                confirmButton: "btn btn-primary"
              }
            });
          }
        })
      );
    } else {
      this.markFormGroupTouched(this.modelCodeForm);
    }
  }
}
